import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form } from 'react-bootstrap';
import {
  FileText, Plus, Trash2, Edit, Eye, Search,
  Type, AlignLeft, ChevronDown, CircleDot, CheckSquare,
  GripVertical, Save,
} from 'lucide-react';
import { PageHeader, AppButton, ConfirmModal } from '../../ui';
import apiClient from '../../../utils/apiClient';
import './form-templates.scss';

const FIELD_TYPES = [
  { type: 'text', label: 'Text', icon: Type },
  { type: 'textarea', label: 'Textarea', icon: AlignLeft },
  { type: 'dropdown', label: 'Dropdown', icon: ChevronDown },
  { type: 'radio', label: 'Radio', icon: CircleDot },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

const DEFAULT_FIELDS = [
  { fieldId: 'name', type: 'text', label: 'Full Name', placeholder: 'Enter your full name', required: true, isDefault: true, defaultField: 'name', options: [], order: 0 },
  { fieldId: 'email', type: 'email', label: 'Email Address', placeholder: 'Enter your email', required: true, isDefault: true, defaultField: 'email', options: [], order: 1 },
  { fieldId: 'phone', type: 'phone', label: 'Phone Number', placeholder: 'Enter your phone number', required: true, isDefault: true, defaultField: 'phone', options: [], order: 2 },
  { fieldId: 'course', type: 'dropdown', label: 'Course', placeholder: 'Select a course', required: false, isDefault: true, defaultField: 'course', options: [], order: 3 },
  { fieldId: 'message', type: 'textarea', label: 'Message', placeholder: 'Enter your message', required: false, isDefault: true, defaultField: 'message', options: [], order: 4 },
];

const CATEGORIES = ['General', 'Education', 'IT Training', 'Coaching', 'Workshops', 'Events'];

const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const FormTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  // Form modal state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formFields, setFormFields] = useState([]);
  const [formSettings, setFormSettings] = useState({
    submitButtonText: 'Submit',
    successMessage: 'Thank you! We will get back to you soon.',
    theme: 'default',
  });
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Preview modal
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/sa/form-templates');
      setTemplates(data.templates || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ─── Open form for create / edit ───────────────────────────────────────
  const openCreateForm = () => {
    setEditingId(null);
    setFormName('');
    setFormDesc('');
    setFormCategory('');
    setFormFields([...DEFAULT_FIELDS]);
    setFormSettings({ submitButtonText: 'Submit', successMessage: 'Thank you! We will get back to you soon.', theme: 'default' });
    setFormError('');
    setShowForm(true);
  };

  const openEditForm = async (template) => {
    setEditingId(template._id);
    setFormName(template.name);
    setFormDesc(template.description || '');
    setFormCategory(template.templateCategory || '');
    setFormFields(template.fields || []);
    setFormSettings(template.settings || { submitButtonText: 'Submit', successMessage: 'Thank you!', theme: 'default' });
    setFormError('');
    setShowForm(true);
  };

  // ─── Save template ─────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      setFormError('Template name is required.');
      return;
    }
    if (formFields.length === 0) {
      setFormError('At least one field is required.');
      return;
    }

    setFormSaving(true);
    setFormError('');

    const payload = {
      name: formName.trim(),
      description: formDesc.trim(),
      category: formCategory,
      fields: formFields,
      settings: formSettings,
    };

    try {
      if (editingId) {
        await apiClient.put(`/sa/form-templates/${editingId}`, payload);
      } else {
        await apiClient.post('/sa/form-templates', payload);
      }
      setShowForm(false);
      fetchTemplates();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save template.');
    }
    setFormSaving(false);
  };

  // ─── Delete ────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await apiClient.delete(`/sa/form-templates/${deleteId}`);
      setDeleteId(null);
      fetchTemplates();
    } catch { /* ignore */ }
  };

  // ─── Field operations ──────────────────────────────────────────────────
  const addField = (type) => {
    setFormFields(prev => [...prev, {
      fieldId: generateFieldId(),
      type,
      label: `${type.charAt(0).toUpperCase() + type.slice(1)} Field`,
      placeholder: '',
      required: false,
      isDefault: false,
      defaultField: null,
      options: type === 'dropdown' || type === 'radio' || type === 'checkbox' ? ['Option 1', 'Option 2'] : [],
      order: prev.length,
    }]);
  };

  const updateField = (index, updates) => {
    setFormFields(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  const removeField = (index) => {
    setFormFields(prev => prev.filter((_, i) => i !== index));
  };

  // ─── Filter ────────────────────────────────────────────────────────────
  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.templateCategory || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="sa-form-templates">
      <PageHeader
        title="Form Templates"
        description="Create enquiry form templates that all institutes can use directly"
        icon={FileText}
        actions={
          <AppButton variant="default" size="sm" onClick={openCreateForm}>
            <Plus size={16} /> Create Template
          </AppButton>
        }
      />

      {/* Stats */}
      <div className="sa-form-templates__stats">
        <div className="sa-form-templates__stat">
          <span className="sa-form-templates__stat-value">{templates.length}</span>
          <span className="sa-form-templates__stat-label">Total Templates</span>
        </div>
        <div className="sa-form-templates__stat sa-form-templates__stat--active">
          <span className="sa-form-templates__stat-value">{templates.filter(t => t.status === 'active').length}</span>
          <span className="sa-form-templates__stat-label">Active</span>
        </div>
        <div className="sa-form-templates__stat sa-form-templates__stat--categories">
          <span className="sa-form-templates__stat-value">{new Set(templates.map(t => t.templateCategory).filter(Boolean)).size}</span>
          <span className="sa-form-templates__stat-label">Categories</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="sa-form-templates__toolbar">
        <div className="sa-form-templates__search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Template cards */}
      {loading ? (
        <div className="text-center py-5">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="sa-form-templates__empty">
          <FileText size={40} strokeWidth={1.5} />
          <h3>No templates yet</h3>
          <p>Create your first form template for institutes to use.</p>
        </div>
      ) : (
        <div className="sa-form-templates__grid">
          {filtered.map(tpl => (
            <div key={tpl._id} className="sa-form-templates__card">
              <div className="sa-form-templates__card-header">
                <h4>{tpl.name}</h4>
                {tpl.templateCategory && (
                  <span className="sa-form-templates__category-badge">{tpl.templateCategory}</span>
                )}
              </div>
              {tpl.description && <p className="sa-form-templates__card-desc">{tpl.description}</p>}
              <div className="sa-form-templates__card-meta">
                <span>{tpl.fields?.length || 0} fields</span>
                <span className={`sa-form-templates__status ${tpl.status}`}>{tpl.status}</span>
              </div>
              <div className="sa-form-templates__card-actions">
                <button onClick={() => setPreviewTemplate(tpl)} title="Preview">
                  <Eye size={14} /> Preview
                </button>
                <button onClick={() => openEditForm(tpl)} title="Edit">
                  <Edit size={14} /> Edit
                </button>
                <button className="sa-form-templates__action--danger" onClick={() => setDeleteId(tpl._id)} title="Delete">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <ConfirmModal
          show={!!deleteId}
          title="Delete Template"
          message="Are you sure you want to delete this template? Existing forms created from it will not be affected."
          onConfirm={handleDelete}
          onHide={() => setDeleteId(null)}
          confirmText="Delete"
          variant="danger"
        />
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <Modal show={!!previewTemplate} onHide={() => setPreviewTemplate(null)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Preview — {previewTemplate.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="sa-form-templates__preview">
              {previewTemplate.fields?.map((field, idx) => (
                <div key={idx} className="sa-form-templates__preview-field">
                  <label>
                    {field.label}
                    {field.required && <span className="sa-form-templates__required">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea placeholder={field.placeholder} disabled />
                  ) : field.type === 'dropdown' ? (
                    <select disabled>
                      <option>{field.placeholder || 'Select...'}</option>
                      {field.options?.map((o, i) => <option key={i}>{o}</option>)}
                    </select>
                  ) : field.type === 'radio' ? (
                    <div className="sa-form-templates__preview-options">
                      {field.options?.map((o, i) => (
                        <label key={i}><input type="radio" disabled /> {o}</label>
                      ))}
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <div className="sa-form-templates__preview-options">
                      {field.options?.map((o, i) => (
                        <label key={i}><input type="checkbox" disabled /> {o}</label>
                      ))}
                    </div>
                  ) : (
                    <input type={field.type === 'email' ? 'email' : 'text'} placeholder={field.placeholder} disabled />
                  )}
                </div>
              ))}
              <button className="sa-form-templates__preview-btn" disabled>
                {previewTemplate.settings?.submitButtonText || 'Submit'}
              </button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/* Create / Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered size="lg" className="sa-form-templates__modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Template' : 'Create Template'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {formError && <div className="sa-form-templates__form-error">{formError}</div>}

            <div className="sa-form-templates__form-grid">
              <Form.Group className="mb-3">
                <Form.Label>Template Name *</Form.Label>
                <Form.Control
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Course Enquiry Form"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select value={formCategory} onChange={(e) => setFormCategory(e.target.value)}>
                  <option value="">No category</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Brief description of this template..."
              />
            </Form.Group>

            {/* Fields */}
            <div className="sa-form-templates__fields-section">
              <h5>Form Fields</h5>
              <div className="sa-form-templates__fields-list">
                {formFields.map((field, idx) => (
                  <div key={field.fieldId} className="sa-form-templates__field-item">
                    <div className="sa-form-templates__field-grip">
                      <GripVertical size={14} />
                    </div>
                    <div className="sa-form-templates__field-body">
                      <div className="sa-form-templates__field-row">
                        <Form.Control
                          size="sm"
                          value={field.label}
                          onChange={(e) => updateField(idx, { label: e.target.value })}
                          placeholder="Field label"
                        />
                        <Form.Select
                          size="sm"
                          value={field.type}
                          onChange={(e) => updateField(idx, { type: e.target.value })}
                          disabled={field.isDefault}
                        >
                          <option value="text">Text</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="textarea">Textarea</option>
                          <option value="dropdown">Dropdown</option>
                          <option value="radio">Radio</option>
                          <option value="checkbox">Checkbox</option>
                        </Form.Select>
                        <Form.Check
                          type="switch"
                          label="Required"
                          checked={field.required}
                          onChange={(e) => updateField(idx, { required: e.target.checked })}
                        />
                      </div>
                      <Form.Control
                        size="sm"
                        className="mt-1"
                        value={field.placeholder}
                        onChange={(e) => updateField(idx, { placeholder: e.target.value })}
                        placeholder="Placeholder text"
                      />
                      {field.type === 'dropdown' && !field.isDefault && (
                        <>
                          <Form.Check
                            type="switch"
                            label="Auto-populate with institute courses"
                            className="mt-1"
                            checked={field.defaultField === 'course'}
                            onChange={(e) => updateField(idx, {
                              defaultField: e.target.checked ? 'course' : null,
                              options: e.target.checked ? [] : (field.options?.length ? field.options : ['Option 1', 'Option 2']),
                            })}
                          />
                          {field.defaultField === 'course' && (
                            <small className="text-muted d-block mt-1">This dropdown will automatically show each institute's course list when they use this template.</small>
                          )}
                        </>
                      )}
                      {['dropdown', 'radio', 'checkbox'].includes(field.type) && !field.isDefault && field.defaultField !== 'course' && (
                        <Form.Control
                          size="sm"
                          className="mt-1"
                          value={(field.options || []).join(', ')}
                          onChange={(e) => updateField(idx, { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })}
                          placeholder="Options (comma-separated)"
                        />
                      )}
                    </div>
                    {!field.isDefault && (
                      <button
                        type="button"
                        className="sa-form-templates__field-remove"
                        onClick={() => removeField(idx)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="sa-form-templates__add-field">
                <span>Add field:</span>
                {FIELD_TYPES.map(ft => (
                  <button key={ft.type} type="button" onClick={() => addField(ft.type)}>
                    <ft.icon size={14} /> {ft.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Settings */}
            <div className="sa-form-templates__settings-section">
              <h5>Settings</h5>
              <div className="sa-form-templates__form-grid">
                <Form.Group className="mb-2">
                  <Form.Label>Submit Button Text</Form.Label>
                  <Form.Control
                    size="sm"
                    value={formSettings.submitButtonText}
                    onChange={(e) => setFormSettings(p => ({ ...p, submitButtonText: e.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Theme</Form.Label>
                  <Form.Select
                    size="sm"
                    value={formSettings.theme}
                    onChange={(e) => setFormSettings(p => ({ ...p, theme: e.target.value }))}
                  >
                    <option value="default">Default</option>
                    <option value="minimal">Minimal</option>
                    <option value="modern">Modern</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <Form.Group className="mb-2">
                <Form.Label>Success Message</Form.Label>
                <Form.Control
                  size="sm"
                  value={formSettings.successMessage}
                  onChange={(e) => setFormSettings(p => ({ ...p, successMessage: e.target.value }))}
                />
              </Form.Group>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <AppButton variant="ghost" onClick={() => setShowForm(false)}>Cancel</AppButton>
            <AppButton type="submit" disabled={formSaving}>
              <Save size={16} /> {formSaving ? 'Saving...' : (editingId ? 'Update Template' : 'Create Template')}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default FormTemplatesPage;
